import { parseISO, format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import store from "@/store";

/**
 * Javascript date object doesn't store timezone info.
 * The date is always displayed in browser timezone.
 * But for printer service (which use headless chorme) it should be school's timezone.
 *
 * @returns {String}
 */
const getSchoolTimeZone = () => {
  if (store.state.isPrint) {
    return window.SchoolSettings.tzname;
  }
  return undefined;
};

/**
 * @param {Date} dt Date
 * @param {String} formatStr Format
 * @returns {String}
 */
export const formatInSchoolTimeZone = (dt, formatStr) => {
  const schoolTZ = getSchoolTimeZone();
  if (schoolTZ) {
    return formatInTimeZone(dt, schoolTZ, formatStr);
  }
  return format(dt, formatStr);
};

const getIsoFormat = (dateOnly) =>
  dateOnly ? "y-MM-dd" : "y-MM-dd'T'HH:mm:ss.SSSSSSXXX";

/**
 * Format date as ISO string
 * @param {Date} dt Date to parse
 * @param {boolean} dateOnly Output has no time
 * @returns {string}
 */
export const isoFormat = (dt, dateOnly) =>
  formatInSchoolTimeZone(dt, getIsoFormat(dateOnly));

/**
 * @returns {String} Current date in school timezone e.g. "2001-04-23"
 */
export const isoSchoolNowFormat = (dateOnly) =>
  formatInTimeZone(new Date(), window.SchoolSettings.tzname, getIsoFormat(dateOnly));

export const parseIsoFormat = (dt, dateOnly) => isoFormat(parseISO(dt), dateOnly);

/**
 * Format date according site style
 * @param {Date} dt Date to format
 * @returns {string}
 */
export const siteDateFormat = (dt) => formatInSchoolTimeZone(dt, "dd MMM y");

/**
 * Reformat server date string into site-according style format.
 * @param {string} date string to reformat
 * @returns {string}
 */
export const parseSiteDateFormat = (date) => siteDateFormat(parseISO(date));

/**
 * Format datetime according site style
 * @param {Date} dt Date to format
 * @returns {string}
 */
export const siteDateTimeFormat = (dt) =>
  formatInSchoolTimeZone(dt, "dd MMM y 'at' HH:mm");

/**
 * Reformat server datetime string into site-according style format.
 * @param {string} dt string to reformat
 * @returns {string}
 */
export const parseSiteDateTimeFormat = (dt) => siteDateTimeFormat(parseISO(dt));

/**
 * Reformat ISO date into site DOB format "dd/MM/y".
 * @param {String|null} dob ISO date string
 * @returns {String}
 */
export const reformatDOB = (dob) => dob && dob.split("-").reverse().join("/");

export const formatJsonTime = (time) => time && `${time.HH}:${time.mm}`;

/**
 * Format date like "Friday 4 December 2021"
 * @param {Date} dt Date to format
 * @returns {string}
 */
export const weekdayWithDateFormat = (dt) => formatInSchoolTimeZone(dt, "EEEE d MMMM y");

export const parseWeekdayWithDateFormat = (date) => weekdayWithDateFormat(parseISO(date));
